// File: webelieve-frontend/src/features/contacts/components/AIInsightsPanel.tsx
// Ultra-Simple Predictive Intake Panel — win/loss probability + transparent sources + one-click actions.
// Lives in the right sidebar of the Contact detail page.

import React, { useState, useEffect } from 'react';
import {
  CpuChipIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentArrowUpIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useClientScorecards, type MatterScorecard, type ScorecardFactor, type SourceRef } from '../hooks/useClientScorecards';
import { useLeadInsights } from '../hooks/useLeadInsights';
import type { LeadDetail } from '@/features/intake/api/intakeCrmApi';

// ─── Props ────────────────────────────────────────────────────

interface AIInsightsPanelProps {
  contactId: string;
  className?: string;
  /** When true, renders a condensed view for the 320px peek sidebar */
  compact?: boolean;
}

interface LeadJourneyViewModel {
  summary?: string;
  keyFacts: string[];
  recommendedActions: string[];
  missingFacts: string[];
  practiceArea?: string;
  matterType?: string;
  urgency?: string;
  riskLevel?: string;
  confidence?: number;
  leadScore?: number;
  estimatedValue?: number;
  documentCount: number;
  activityCount: number;
  updatedAt?: string;
}

// ─── Helpers ──────────────────────────────────────────────────

function formatCurrency(value: number | null | undefined): string {
  if (value == null || value === 0) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
}

function getWinColor(pct: number): string {
  if (pct >= 70) return 'text-green-500 dark:text-green-400';
  if (pct >= 50) return 'text-yellow-500 dark:text-yellow-400';
  return 'text-red-500 dark:text-red-400';
}

function getLossColor(pct: number): string {
  if (pct <= 30) return 'text-green-600 dark:text-green-500';
  if (pct <= 50) return 'text-yellow-600 dark:text-yellow-500';
  return 'text-red-600 dark:text-red-500';
}

function getConfidenceLabel(level: string | null | undefined, similarCases: number, completenessScore: number): string {
  const caseText = similarCases > 0 ? `${similarCases.toLocaleString()} comparable cases` : 'base rate estimates';
  const fileText = completenessScore > 0 ? `${Math.round(completenessScore)}% of file complete` : 'minimal file data';
  return `${level === 'high' ? 'High' : level === 'medium' ? 'Medium' : 'Low'} (based on ${caseText} + ${fileText})`;
}

function getBottomLineRecommendation(winPct: number, riskLevel: string, profitProjection: number | null): string {
  if (winPct >= 75) {
    return `Strong case — high likelihood of favorable outcome${profitProjection && profitProjection > 0 ? ` with ${formatCurrency(profitProjection)} projected firm profit` : ''}. Accept and move forward.`;
  }
  if (winPct >= 60) {
    return `Promising case with moderate confidence. Filling data gaps could push probability higher. Consider accepting with priority on evidence gathering.`;
  }
  if (winPct >= 45) {
    return `Borderline case — outcome uncertain. Critical missing data could shift the analysis significantly. Gather key evidence before committing resources.`;
  }
  if (winPct >= 30) {
    return `Challenging case with below-average odds. ${riskLevel === 'high' || riskLevel === 'very_high' ? 'Multiple risk factors present. ' : ''}Careful cost-benefit analysis recommended before proceeding.`;
  }
  return `High-risk case with unfavorable odds. Declining or restructuring the fee arrangement is recommended unless significant new evidence emerges.`;
}

function startCase(value?: string): string | undefined {
  if (!value) return undefined;
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizeList(values: unknown): string[] {
  return Array.isArray(values)
    ? values.filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    : [];
}

function deriveLeadJourney(lead: LeadDetail | null): LeadJourneyViewModel | null {
  if (!lead) return null;

  const intakeResponses = lead.intakeFormResponses || {};
  const aiDerived = intakeResponses.aiDerived || {};
  const aiClassification = lead.aiClassification || intakeResponses.aiClassification || {};
  const synopsis = lead.synopsis;

  const summary =
    typeof synopsis === 'string'
      ? synopsis
      : synopsis?.summary
        || synopsis?.caseAnalysis?.estimatedOutcome
        || aiDerived.reasoning
        || aiClassification.reasoning
        || lead.intakeNotes
        || undefined;

  const keyFacts = [
    ...normalizeList(synopsis?.key_facts),
    ...normalizeList(synopsis?.caseAnalysis?.legalIssues),
    ...normalizeList(synopsis?.documentAnalysis?.keyFactsFromDocuments),
  ].slice(0, 6);

  const recommendedActions = [
    ...normalizeList(synopsis?.nextSteps?.immediateActions),
    ...normalizeList(synopsis?.caseAnalysis?.recommendedActions),
    ...normalizeList(aiDerived.suggestedActions),
  ].slice(0, 5);

  const missingFacts = normalizeList(aiDerived.missingCriticalFields).slice(0, 5);

  return {
    summary,
    keyFacts,
    recommendedActions,
    missingFacts,
    practiceArea: startCase(
      synopsis?.recommended_practice_area
      || synopsis?.caseAnalysis?.practiceArea
      || aiClassification.practiceArea
      || aiDerived.practiceArea
      || lead.practiceArea
    ),
    matterType: startCase(aiClassification.matterType || aiDerived.matterType),
    urgency: startCase(aiClassification.urgencyLevel || aiDerived.urgencyLevel),
    riskLevel: startCase(synopsis?.risk_level || synopsis?.riskAssessment?.riskLevel),
    confidence: typeof aiDerived.confidence === 'number'
      ? aiDerived.confidence
      : typeof synopsis?.confidence === 'number'
        ? synopsis.confidence
        : undefined,
    leadScore: lead.leadScore,
    estimatedValue: typeof aiDerived.estimatedValue === 'number' ? aiDerived.estimatedValue : undefined,
    documentCount: lead.documentCount || lead.documents?.length || 0,
    activityCount: lead.activity?.length || 0,
    updatedAt: lead.updatedAt,
  };
}

// ─── Provenance Helpers ──────────────────────────────────────

const SOURCE_TYPE_LABELS: Record<string, string> = {
  matter_field: 'Matter Record',
  document_metadata: 'Document',
  document_content: 'Document Text',
  party_relationship: 'Party Record',
  intake_submission: 'Intake Form',
  llm_extraction: 'AI Analysis',
  public_prior: 'Public Data',
  practice_area_config: 'Practice Config',
  computed: 'Calculated',
  bayesian_update: 'Bayesian Model',
};

function formatSourceRef(ref?: SourceRef): string | null {
  if (!ref) return null;
  const parts: string[] = [];
  parts.push(SOURCE_TYPE_LABELS[ref.type] || ref.type);
  if (ref.entity_label) parts.push(`"${ref.entity_label}"`);
  if (ref.field) parts.push(`[${ref.field}]`);
  if (ref.excerpt) parts.push(`— ${ref.excerpt}`);
  return parts.join(' ');
}

// ─── Source Table Types ──────────────────────────────────────

interface SourceRow {
  sourceType: 'Public Data' | 'Your Data' | 'Missing';
  description: string;
  impactOnOdds: string;
  impactClass: string;
  sourceRef?: SourceRef;
}

function buildSourceTable(sc: MatterScorecard): SourceRow[] {
  const rows: SourceRow[] = [];

  // 1. Public Data — from public_prior_data and similar_case_count
  const priors = sc.public_prior_data as Record<string, any> | null;
  const jurisdiction = sc.venue_judge_benchmarks?.jurisdiction || priors?.jurisdiction || 'general';
  const practiceArea = sc.matter?.practice_area || priors?.practice_area || 'litigation';
  const similarCases = sc.similar_case_count || (priors?.similar_case_count ?? 0);
  const baseRate = priors?.plaintiff_win_rate ? Math.round(priors.plaintiff_win_rate * 100) : null;

  rows.push({
    sourceType: 'Public Data',
    description: `${similarCases > 0 ? similarCases.toLocaleString() : 'Estimated'} similar ${practiceArea.replace(/_/g, ' ')} cases${jurisdiction !== 'general' ? ` in ${jurisdiction}` : ''}`,
    impactOnOdds: baseRate != null ? `Base: ${baseRate}%` : 'Base rate applied',
    impactClass: 'text-gray-600 dark:text-gray-400',
    sourceRef: { type: 'public_prior', field: 'plaintiff_win_rate', excerpt: `${jurisdiction} ${practiceArea} base rates` },
  });

  // Show document content stats if available
  const evidence = sc.case_evidence_data as Record<string, any> | null;
  const docsWithContent = (evidence?.documents_used as any[] | undefined)?.filter((d: any) => d.had_content)?.length ?? 0;
  const totalDocs = evidence?.documents_analyzed ?? 0;
  if (totalDocs > 0) {
    rows.push({
      sourceType: 'Your Data',
      description: docsWithContent > 0
        ? `${totalDocs} documents analyzed (${docsWithContent} with full text)`
        : `${totalDocs} documents analyzed (metadata only)`,
      impactOnOdds: evidence?.document_content_chars
        ? `${Math.round(evidence.document_content_chars / 1000)}k chars`
        : 'Titles only',
      impactClass: 'text-lex-accent',
      sourceRef: { type: docsWithContent > 0 ? 'document_content' : 'document_metadata', field: 'extracted_text' },
    });
  }

  // 2. Your Data — extract positive/negative factors from supporting metrics (with provenance)
  const allFactors: ScorecardFactor[] = [];
  if (sc.liability_strength?.factors) allFactors.push(...sc.liability_strength.factors);
  if (sc.damages_injury_profile?.factors) allFactors.push(...sc.damages_injury_profile.factors);
  if (sc.opposing_party_insights?.factors) allFactors.push(...sc.opposing_party_insights.factors);
  if (sc.client_plaintiff_profile?.factors) allFactors.push(...sc.client_plaintiff_profile.factors);
  if (sc.venue_judge_benchmarks?.factors) allFactors.push(...sc.venue_judge_benchmarks.factors);

  const positiveFactors = allFactors.filter(f => f.impact === 'positive');
  const negativeFactors = allFactors.filter(f => f.impact === 'negative');

  positiveFactors.slice(0, 5).forEach(f => {
    rows.push({
      sourceType: 'Your Data',
      description: f.factor,
      impactOnOdds: `+${f.points}%`,
      impactClass: 'text-green-600 dark:text-green-400 font-medium',
      sourceRef: f.source_ref,
    });
  });

  negativeFactors.slice(0, 3).forEach(f => {
    rows.push({
      sourceType: 'Your Data',
      description: f.factor,
      impactOnOdds: `${f.points}%`,
      impactClass: 'text-red-600 dark:text-red-400 font-medium',
      sourceRef: f.source_ref,
    });
  });

  // 3. Missing — from data_completeness.recommendations (highest impact first)
  const recs = sc.data_completeness?.recommendations || [];
  recs
    .sort((a: any, b: any) => (b.impact_points || 0) - (a.impact_points || 0))
    .slice(0, 3)
    .forEach((rec: any) => {
      rows.push({
        sourceType: 'Missing',
        description: rec.action || rec.field_key,
        impactOnOdds: rec.impact_points > 0 ? `Would add +${rec.impact_points}%` : 'Would improve score',
        impactClass: 'text-amber-600 dark:text-amber-400 italic',
        sourceRef: rec.source_ref,
      });
    });

  return rows;
}

const LeadJourneyCard: React.FC<{ lead: LeadDetail; compact: boolean }> = ({ lead, compact }) => {
  const journey = deriveLeadJourney(lead);

  if (!journey) return null;

  const summaryText = journey.summary?.trim() || 'AI synopsis will deepen as intake facts, notes, and documents are added to this contact.';

  return (
    <div className="border border-lex-accent/20 rounded-xl bg-gradient-to-br from-lex-accent-soft to-white dark:from-gray-800 dark:to-gray-800 overflow-hidden">
      <div className={compact ? 'p-3 space-y-3' : 'p-4 space-y-4'}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-wide font-semibold text-lex-accent">Lead Journey</p>
            <h4 className={`font-semibold text-gray-900 dark:text-gray-100 ${compact ? 'text-sm' : 'text-base'}`}>
              Full Intake Perspective
            </h4>
          </div>
          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-white/80 dark:bg-gray-900/40 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
            {startCase(lead.status) || 'Lead'}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg bg-white/90 dark:bg-gray-900/40 p-2.5 border border-white dark:border-gray-700">
            <p className="text-[10px] uppercase font-medium text-gray-500 dark:text-gray-400">Lead Score</p>
            <p className={`text-xl font-bold ${getWinColor(journey.leadScore ?? 0)}`}>
              {journey.leadScore != null ? `${Math.round(journey.leadScore)}%` : '—'}
            </p>
          </div>
          <div className="rounded-lg bg-white/90 dark:bg-gray-900/40 p-2.5 border border-white dark:border-gray-700">
            <p className="text-[10px] uppercase font-medium text-gray-500 dark:text-gray-400">Practice Area</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight">
              {journey.practiceArea || 'Pending Analysis'}
            </p>
          </div>
          <div className="rounded-lg bg-white/90 dark:bg-gray-900/40 p-2.5 border border-white dark:border-gray-700">
            <p className="text-[10px] uppercase font-medium text-gray-500 dark:text-gray-400">Urgency / Risk</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {journey.urgency || journey.riskLevel || 'Monitoring'}
            </p>
          </div>
          <div className="rounded-lg bg-white/90 dark:bg-gray-900/40 p-2.5 border border-white dark:border-gray-700">
            <p className="text-[10px] uppercase font-medium text-gray-500 dark:text-gray-400">Confidence</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {journey.confidence != null ? `${Math.round(journey.confidence)}%` : 'Evolving'}
            </p>
          </div>
        </div>

        <div className="rounded-lg bg-white/75 dark:bg-gray-900/30 p-3 border border-white dark:border-gray-700">
          <p className="text-[10px] uppercase font-medium text-gray-500 dark:text-gray-400 mb-1">AI Synopsis</p>
          <p className={`text-gray-800 dark:text-gray-200 leading-relaxed ${compact ? 'text-xs' : 'text-sm'}`}>
            {summaryText}
          </p>
        </div>

        {(journey.keyFacts.length > 0 || journey.recommendedActions.length > 0) && (
          <div className="grid gap-3">
            {journey.keyFacts.length > 0 && (
              <div>
                <p className="text-[10px] uppercase font-medium text-gray-500 dark:text-gray-400 mb-1.5">Key Facts</p>
                <ul className="space-y-1">
                  {journey.keyFacts.map((fact, index) => (
                    <li key={`${fact}-${index}`} className={`text-gray-700 dark:text-gray-300 ${compact ? 'text-xs' : 'text-sm'}`}>
                      {fact}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {journey.recommendedActions.length > 0 && (
              <div>
                <p className="text-[10px] uppercase font-medium text-gray-500 dark:text-gray-400 mb-1.5">Next Best Actions</p>
                <div className="space-y-1.5">
                  {journey.recommendedActions.map((action, index) => (
                    <div key={`${action}-${index}`} className="rounded-md bg-white/80 dark:bg-gray-900/30 px-2.5 py-2 border border-white dark:border-gray-700">
                      <p className={`text-gray-800 dark:text-gray-200 ${compact ? 'text-xs' : 'text-sm'}`}>{action}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {journey.missingFacts.length > 0 && (
          <div className="rounded-lg border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/10 p-3">
            <p className="text-[10px] uppercase font-medium text-amber-700 dark:text-amber-300 mb-1.5">Missing Inputs That Would Strengthen Analysis</p>
            <div className="flex flex-wrap gap-1.5">
              {journey.missingFacts.map((field) => (
                <span key={field} className="px-2 py-0.5 rounded-full text-[10px] bg-white dark:bg-gray-900/40 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-700">
                  {startCase(field)}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 text-[10px] text-gray-500 dark:text-gray-400 pt-1 border-t border-white/70 dark:border-gray-700">
          <span>{journey.documentCount} document{journey.documentCount === 1 ? '' : 's'}</span>
          <span>{journey.activityCount} activity event{journey.activityCount === 1 ? '' : 's'}</span>
          {journey.matterType && <span>{journey.matterType}</span>}
          {journey.estimatedValue != null && <span>Est. value {formatCurrency(journey.estimatedValue)}</span>}
        </div>
      </div>
    </div>
  );
};

// ─── Single Matter Card ──────────────────────────────────────

interface MatterCardProps {
  scorecard: MatterScorecard;
  isExpanded: boolean;
  onToggle: () => void;
  onRefresh: (matterId: string) => void;
}

const MatterCard: React.FC<MatterCardProps> = ({ scorecard, isExpanded, onToggle, onRefresh }) => {
  const sc = scorecard;
  const matterTitle = sc.matter?.title || sc.matter?.displayNumber || 'Untitled Matter';
  const practiceArea = sc.matter?.practice_area?.replace(/_/g, ' ') || '';

  // Win/loss probabilities
  const winPct = Math.round((sc.profitability_probability ?? 0.5) * 100);
  const lossPct = 100 - winPct;

  // Bottom-line recommendation
  const recommendation = getBottomLineRecommendation(winPct, sc.risk_level, sc.firm_profit_projection);

  // Source table
  const sourceRows = buildSourceTable(sc);

  // Confidence
  const completenessScore = sc.data_completeness?.score ?? 0;
  const confidenceText = getConfidenceLabel(sc.confidence_level, sc.similar_case_count, completenessScore);

  // Missing items for action buttons
  const missingActions = (sc.data_completeness?.recommendations || [])
    .sort((a: any, b: any) => (b.impact_points || 0) - (a.impact_points || 0))
    .slice(0, 3);

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-800">
      {/* Collapsed Header */}
      <button
        onClick={onToggle}
        className="w-full text-left p-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
      >
        {/* Matter Title + Practice Area */}
        <div className="flex items-start justify-between mb-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight">{matterTitle}</p>
            {practiceArea && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 capitalize">{practiceArea}</p>
            )}
          </div>
          {isExpanded
            ? <ChevronUpIcon className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />
            : <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />}
        </div>

        {/* Win / Loss Row — always visible */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Win Probability</p>
            <p className={`text-3xl font-extrabold tracking-tight ${getWinColor(winPct)}`}>
              {winPct}%
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500 dark:text-gray-400">Loss / Unprofitable</p>
            <p className={`text-xl font-bold ${getLossColor(lossPct)}`}>
              {lossPct}%
            </p>
          </div>
        </div>

        {/* Win probability bar */}
        <div className="mt-2 w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5 overflow-hidden">
          <div
            className={`h-2.5 rounded-full transition-all duration-500 ${winPct >= 70 ? 'bg-green-500' : winPct >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
            style={{ width: `${winPct}%` }}
          />
        </div>
      </button>

      {/* Expanded Detail */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700 space-y-4">

          {/* Bottom-line recommendation */}
          <div className="bg-lex-accent-soft rounded-lg p-3 mt-3">
            <p className="text-sm text-lex-accent font-medium leading-snug">
              {recommendation}
            </p>
          </div>

          {/* How We Calculated This — source table */}
          <div>
            <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">How We Calculated This</p>
            <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700/50">
                    <th className="text-left px-2.5 py-1.5 font-medium text-gray-500 dark:text-gray-400">Source</th>
                    <th className="text-left px-2.5 py-1.5 font-medium text-gray-500 dark:text-gray-400">What Was Used</th>
                    <th className="text-right px-2.5 py-1.5 font-medium text-gray-500 dark:text-gray-400">Impact</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {sourceRows.map((row, i) => {
                    const tooltip = formatSourceRef(row.sourceRef);
                    return (
                      <tr key={i} className={`group ${row.sourceType === 'Missing' ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''}`}>
                        <td className="px-2.5 py-2 text-gray-600 dark:text-gray-400 whitespace-nowrap font-medium">
                          {row.sourceType}
                        </td>
                        <td className="px-2.5 py-2 text-gray-700 dark:text-gray-300 relative">
                          <span title={tooltip ?? undefined} className={tooltip ? 'cursor-help border-b border-dotted border-gray-300 dark:border-gray-600' : ''}>
                            {row.description}
                          </span>
                          {tooltip && (
                            <span className="hidden group-hover:block absolute left-0 top-full z-20 mt-1 px-2.5 py-1.5 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-[10px] rounded-md shadow-lg max-w-xs whitespace-normal leading-tight">
                              {tooltip}
                            </span>
                          )}
                        </td>
                        <td className={`px-2.5 py-2 text-right whitespace-nowrap ${row.impactClass}`}>
                          {row.impactOnOdds}
                        </td>
                      </tr>
                    );
                  })}
                  {sourceRows.length === 0 && (
                    <tr>
                      <td colSpan={3} className="px-2.5 py-3 text-center text-gray-400 italic">
                        Source analysis pending...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Data Sources Summary */}
          {(() => {
            const ev = sc.case_evidence_data as Record<string, any> | null;
            const parts: string[] = [];
            if (ev?.documents_analyzed) parts.push(`${ev.documents_analyzed} docs`);
            if (ev?.document_content_chars) parts.push(`${Math.round(ev.document_content_chars / 1000)}k chars analyzed`);
            if ((ev?.parties_used as any[])?.length) parts.push(`${(ev!.parties_used as any[]).length} parties`);
            if (ev?.intake_submission_used) parts.push('intake form');
            return parts.length > 0 ? (
              <p className="text-[10px] text-gray-400 dark:text-gray-500">
                Data sources: {parts.join(' + ')}
              </p>
            ) : null;
          })()}

          {/* Confidence */}
          <p className="text-xs text-gray-500 dark:text-gray-400">
            <span className="font-medium">Total confidence in this prediction:</span>{' '}
            {confidenceText}
          </p>

          {/* Recovery & Profit — compact */}
          {(sc.expected_recovery_median || sc.firm_profit_projection) && (
            <div className="flex gap-3">
              {sc.expected_recovery_median && (
                <div className="flex-1 bg-gray-50 dark:bg-gray-700/40 rounded-lg px-3 py-2">
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase font-medium">Expected Recovery</p>
                  <p className="text-sm font-bold text-gray-900 dark:text-gray-100">{formatCurrency(sc.expected_recovery_median)}</p>
                  <p className="text-[10px] text-gray-400">{formatCurrency(sc.expected_recovery_low)} – {formatCurrency(sc.expected_recovery_high)}</p>
                </div>
              )}
              {sc.firm_profit_projection && (
                <div className="flex-1 bg-gray-50 dark:bg-gray-700/40 rounded-lg px-3 py-2">
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase font-medium">Firm Profit</p>
                  <p className="text-sm font-bold text-gray-900 dark:text-gray-100">{formatCurrency(sc.firm_profit_projection)}</p>
                  {sc.firm_roi_multiple != null && (
                    <p className="text-[10px] text-gray-400">ROI: {Number(sc.firm_roi_multiple).toFixed(1)}x</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* One-Click Actions */}
          {missingActions.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">Raise Your Odds</p>
              {missingActions.map((action: any, i: number) => (
                <button
                  key={i}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-lex-accent bg-lex-accent-soft hover:bg-lex-state-hover transition-colors text-left"
                >
                  <div className="flex items-center space-x-2 min-w-0">
                    {action.field_key?.includes('upload') || action.field_key?.includes('medical') || action.field_key?.includes('police')
                      ? <DocumentArrowUpIcon className="h-4 w-4 text-lex-accent flex-shrink-0" />
                      : action.field_key?.includes('search') || action.field_key?.includes('defendant')
                        ? <MagnifyingGlassIcon className="h-4 w-4 text-lex-accent flex-shrink-0" />
                        : <PencilSquareIcon className="h-4 w-4 text-lex-accent flex-shrink-0" />}
                    <span className="text-xs text-lex-accent font-medium truncate">
                      {action.action || action.recommendation}
                    </span>
                  </div>
                  {action.impact_points > 0 && (
                    <span className="text-xs text-green-600 dark:text-green-400 font-bold flex-shrink-0 ml-2">
                      +{action.impact_points}%
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Refresh + Audit */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700">
            <div className="text-[10px] text-gray-400">
              {sc.bayesian_update_count > 1
                ? `Updated ${sc.bayesian_update_count} times`
                : 'Initial analysis'}
              {sc.triggered_by && ` · ${sc.triggered_by.replace(/_/g, ' ')}`}
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); onRefresh(sc.matter_id); }}
              className="flex items-center space-x-1 text-xs text-lex-accent hover:text-lex-accent-hover font-medium"
            >
              <ArrowPathIcon className="h-3.5 w-3.5" />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Panel ──────────────────────────────────────────────

const AIInsightsPanel: React.FC<AIInsightsPanelProps> = ({ contactId, className = '', compact = false }) => {
  const { scorecards, summary, loading, error, fetchScorecards, refreshMatterScorecard, computeAllScorecards } = useClientScorecards(contactId);
  const { lead, loading: leadLoading, error: leadError, fetchLeadInsights } = useLeadInsights(contactId);
  const [expanded, setExpanded] = useState(true);
  const [expandedMatter, setExpandedMatter] = useState<string | null>(null);

  // Auto-expand the first matter once scorecards load
  useEffect(() => {
    if (scorecards.length > 0 && expandedMatter === null) {
      setExpandedMatter(scorecards[0].matter_id);
    }
  }, [scorecards]); // eslint-disable-line react-hooks/exhaustive-deps

  const isRefreshing = loading || leadLoading;

  const handleRefreshAll = async () => {
    await Promise.allSettled([fetchScorecards(), fetchLeadInsights()]);
  };

  // Loading
  if (loading && leadLoading && scorecards.length === 0 && !lead) {
    return (
      <div className={`bg-white dark:bg-gray-800 overflow-y-auto ${className}`}>
        <div className="p-4">
          <div className="flex items-center space-x-2 mb-4">
            <CpuChipIcon className="h-5 w-5 text-lex-accent animate-pulse" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">AI Insights</h3>
          </div>
          <div className="space-y-4">
            <div className="animate-pulse space-y-3">
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
              <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
              <div className="h-2.5 bg-gray-200 dark:bg-gray-700 rounded-full w-full" />
              <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded-lg" />
              <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg" />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center animate-pulse">
              Building the AI journey for this contact...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-800 overflow-y-auto ${className}`}>
      {/* Header */}
      <div className={`sticky top-0 z-10 bg-white dark:bg-gray-800 ${compact ? 'px-3 py-2' : 'p-4'} border-b border-gray-200 dark:border-gray-700`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <CpuChipIcon className={compact ? 'h-4 w-4 text-lex-accent' : 'h-5 w-5 text-lex-accent'} />
            <h3 className={`font-semibold text-gray-900 dark:text-gray-100 ${compact ? 'text-sm' : 'text-lg'}`}>AI Insights</h3>
            {summary.totalMatters > 0 && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                · {summary.totalMatters} matter{summary.totalMatters > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleRefreshAll}
              disabled={isRefreshing}
              title="Refresh all"
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <ArrowPathIcon className={`h-4 w-4 text-gray-500 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={() => setExpanded(!expanded)}>
              {expanded
                ? <ChevronUpIcon className="h-4 w-4 text-gray-500" />
                : <ChevronDownIcon className="h-4 w-4 text-gray-500" />}
            </button>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="p-4 space-y-3">
          {(error || leadError) && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="h-4 w-4 text-red-400 mt-0.5 mr-2 flex-shrink-0" />
                <div className="space-y-1">
                  {leadError && <p className="text-xs text-red-800 dark:text-red-400">{leadError}</p>}
                  {error && <p className="text-xs text-red-800 dark:text-red-400">{error}</p>}
                </div>
              </div>
            </div>
          )}

          {lead && <LeadJourneyCard lead={lead} compact={compact} />}

          {!lead && !leadLoading && !leadError && (
            <div className="rounded-xl border border-dashed border-gray-200 dark:border-gray-700 p-4 text-center">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Lead Journey Not Available Yet</p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                The intake AI view will appear here as lead details, notes, and documents accumulate.
              </p>
            </div>
          )}

          <div className="pt-1">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-[10px] uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400">Matter Analytics</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Scorecards And Case Economics</p>
              </div>
              {scorecards.length > 0 && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {scorecards.length} active scorecard{scorecards.length === 1 ? '' : 's'}
                </span>
              )}
            </div>
          </div>

          {/* Empty state */}
          {scorecards.length === 0 && !loading && !error && (
            <div className="text-center py-8 border border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
              <CpuChipIcon className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">No Matter Scorecards Yet</p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1 max-w-xs mx-auto">
                The lead journey above is already active. Matter scorecards will appear here once a matter exists, or you can recompute if one has already been opened.
              </p>
              <button
                onClick={computeAllScorecards}
                className="mt-4 px-4 py-2 bg-lex-accent hover:bg-lex-accent-hover text-lex-accent-contrast text-sm font-medium rounded-lg transition-colors"
              >
                Compute Scorecards
              </button>
            </div>
          )}

          {/* Client-level summary (only if multiple matters) */}
          {summary.totalMatters > 1 && (
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700/40 dark:to-gray-700/20 rounded-xl p-3 flex items-center justify-between">
              <div>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase font-medium">Portfolio Avg Win Probability</p>
                <p className={`text-2xl font-extrabold ${getWinColor(summary.averageCompositeScore)}`}>
                  {summary.averageCompositeScore}%
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase font-medium">Total Recovery (est.)</p>
                <p className="text-sm font-bold text-gray-900 dark:text-gray-100">
                  {formatCurrency(summary.totalProjectedRecovery.high)}
                </p>
              </div>
            </div>
          )}

          {/* Per-Matter Cards — in compact/peek mode, default all collapsed */}
          {scorecards.map((sc, idx) => (
            <MatterCard
              key={sc.scorecard_id}
              scorecard={sc}
              isExpanded={
                compact
                  ? expandedMatter === sc.matter_id
                  : expandedMatter === sc.matter_id || (expandedMatter === null && idx === 0 && scorecards.length === 1)
              }
              onToggle={() => setExpandedMatter(expandedMatter === sc.matter_id ? null : sc.matter_id)}
              onRefresh={refreshMatterScorecard}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AIInsightsPanel;
